import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlanCatalogEntry, listPlans, loadSession } from "../lib/api.js";

type PlanStatus =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; plans: PlanCatalogEntry[] };

function effectivePrice(plan: PlanCatalogEntry): number {
  if (plan.waiveFees) return 0;
  return plan.effectivePriceMonthlyUsd ?? plan.priceMonthlyUsd;
}

function formatPrice(plan: PlanCatalogEntry): string {
  const price = effectivePrice(plan);
  if (price === 0) return "Free";
  return `$${price.toLocaleString()}/mo`;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}k`;
  return n.toLocaleString();
}

function formatRetention(days: number): string {
  if (days >= 365) {
    const years = Math.round(days / 365);
    return `${years} yr${years === 1 ? "" : "s"}`;
  }
  return `${days} days`;
}

type LimitRow = { label: string; value: (plan: PlanCatalogEntry) => string };
type CapabilityRow = { label: string; key: keyof PlanCatalogEntry["capabilities"] };

const LIMIT_ROWS: LimitRow[] = [
  { label: "Seats (users)", value: (p) => formatNumber(p.limits.seats) },
  { label: "DHIS2 instances", value: (p) => formatNumber(p.limits.dhis2Instances) },
  { label: "Datasets", value: (p) => formatNumber(p.limits.datasets) },
  { label: "Public portals", value: (p) => formatNumber(p.limits.publicPortals) },
  { label: "Scheduled jobs", value: (p) => formatNumber(p.limits.scheduledJobs) },
  { label: "Custom reports", value: (p) => formatNumber(p.limits.reports) },
  { label: "Scheduled reports", value: (p) => formatNumber(p.limits.scheduledReports) },
  { label: "External integrations", value: (p) => formatNumber(p.limits.externalIntegrations) },
  { label: "API calls / month", value: (p) => formatNumber(p.limits.apiCallsPerMonth) },
  { label: "Data retention", value: (p) => formatRetention(p.limits.dataRetentionDays) },
  { label: "Warehouse rows / query", value: (p) => formatNumber(p.limits.warehouseQueryRowsPerRun) },
  { label: "Storage", value: (p) => `${formatNumber(p.limits.storageGb)} GB` }
];

const CAPABILITY_ROWS: CapabilityRow[] = [
  { label: "Warehouse explorer", key: "warehouseExplorer" },
  { label: "Monitoring UI", key: "monitoringUi" },
  { label: "Scheduling engine", key: "scheduling" },
  { label: "Visualisation platform", key: "visualisationPlatform" },
  { label: "Custom reports", key: "customReports" },
  { label: "Scheduled reports", key: "scheduledReports" },
  { label: "External integrations", key: "externalIntegrations" },
  { label: "Public portal", key: "publicPortal" },
  { label: "Private portal", key: "privatePortal" },
  { label: "Priority job queue", key: "priorityJobQueue" },
  { label: "SSO / SAML / OIDC", key: "sso" },
  { label: "Audit log export", key: "auditLogExport" },
  { label: "Multi-region warehouse", key: "multiRegion" },
  { label: "Premium support", key: "premiumSupport" }
];

export default function Landing() {
  const navigate = useNavigate();
  const session = loadSession();
  const [planState, setPlanState] = useState<PlanStatus>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const plans = await listPlans();
        if (cancelled) return;
        setPlanState({ status: "ready", plans });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Unable to load plans";
        setPlanState({ status: "error", message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onSelectPlan = (planId: string) => {
    navigate(`/signup?plan=${encodeURIComponent(planId)}`);
  };

  return (
    <div className="landing">
      <header className="landing-bar">
        <Link to="/" className="brand" style={{ color: "#fff" }}>
          <img src="/brand-logo-light.svg" alt="TallyStream" />
        </Link>
        <nav>
          <a href="#features">Features</a>
          <a href="#pricing">Pricing</a>
          <a href="https://dhis2.org" target="_blank" rel="noreferrer">DHIS2</a>
          {session ? (
            <Link to="/app">Console</Link>
          ) : (
            <>
              <Link to="/login">Sign in</Link>
              <Link to="/signup" className="btn btn-primary" style={{ padding: "6px 12px" }}>
                Sign up
              </Link>
            </>
          )}
        </nav>
      </header>

      <section className="landing-hero">
        <div className="inner">
          <div>
            <h1>Stream tracker events into clean DHIS2 aggregate values.</h1>
            <p>
              TallyStream connects to your DHIS2 servers, tallies tracker and event data
              into the data elements and category-option combos you actually report on,
              and pushes the aggregates back through the DHIS2 dataValueSets API — on a
              schedule, with full lineage and replay.
            </p>
            <div className="cta">
              <Link to="/signup" className="btn btn-primary">Create a workspace</Link>
              <a href="#features" className="btn">See features</a>
              {session ? (
                <Link to="/app" className="btn">Open console</Link>
              ) : (
                <Link to="/login" className="btn">Sign in</Link>
              )}
            </div>
          </div>

          <div className="hero-card">
            <h3>The TallyStream pipeline</h3>
            <ul>
              <li><strong>Pull</strong> tracker events &amp; enrolments from DHIS2 (REST + analytics)</li>
              <li><strong>Tally</strong> with versioned primitives, period mappings &amp; org-unit rules</li>
              <li><strong>Aggregate</strong> into data elements × COCs × period × org unit</li>
              <li><strong>Publish</strong> back to DHIS2 dataValueSets, plus a queryable warehouse</li>
              <li><strong>Audit &amp; replay</strong> any run to a prior metadata snapshot</li>
            </ul>
          </div>
        </div>
      </section>

      <section id="features" className="landing-features">
        <div className="feature">
          <h3>DHIS2 sync workers</h3>
          <p>
            Background workers pull tracker events, enrolments, programs, and aggregate
            data values from any number of DHIS2 servers — incrementally, with checkpoint
            resume, exponential back-off and a dead-letter queue for poison records.
          </p>
        </div>
        <div className="feature">
          <h3>Tally &amp; aggregation engine</h3>
          <p>
            Define primitives, tally rules, period &amp; org-unit mappings once. The engine
            rolls events into <code>dataElement × categoryOptionCombo × period ×
            orgUnit</code> aggregates, ready for DHIS2 dataValueSets push or warehouse
            queries.
          </p>
        </div>
        <div className="feature">
          <h3>Indicators &amp; reports</h3>
          <p>
            Compose numerator/denominator indicators on top of tallies. Author custom
            reports with parameters, schedule them, deliver via email or webhook, and
            attach versioned snapshots.
          </p>
        </div>
        <div className="feature">
          <h3>Warehouse explorer</h3>
          <p>
            Aggregate output lands in a ClickHouse-backed warehouse with row-level access
            control. Run ad-hoc queries, pivot by org-unit hierarchy, and export to CSV
            or Parquet without round-tripping DHIS2.
          </p>
        </div>
        <div className="feature">
          <h3>Scheduling &amp; replay</h3>
          <p>
            Cron schedules per pipeline, manual run-now, async job tracking with live
            progress, and full <em>replay against a prior metadata snapshot</em> so you
            can re-run an old period with the rules that were in force at the time.
          </p>
        </div>
        <div className="feature">
          <h3>Public &amp; private portals</h3>
          <p>
            Publish curated indicators as public dashboards (no DHIS2 login required) or
            private workspace portals — both backed by the same governed warehouse and
            row-level access policies.
          </p>
        </div>
        <div className="feature">
          <h3>Multi-DHIS2 instance management</h3>
          <p>
            Register and rotate credentials for multiple DHIS2 servers (PAT, OAuth2,
            cookie). Encrypted server-side vault, per-instance health checks, and
            connection diagnostics built in.
          </p>
        </div>
        <div className="feature">
          <h3>Forwarding &amp; integrations</h3>
          <p>
            Outbound forwarding to webhooks, S3 buckets, Kafka topics, or another DHIS2
            instance. Inbound webhook signing keys, rate limits and per-tenant quotas
            keep external integrations safe.
          </p>
        </div>
        <div className="feature">
          <h3>Audit, lineage &amp; security</h3>
          <p>
            Every metadata change, sync run, push and login is auditable. Versioned
            metadata snapshots give you point-in-time lineage. SSO (OIDC/SAML), RBAC
            and tenant-scoped encryption keys ship in the box.
          </p>
        </div>
      </section>

      <section id="pricing" className="pricing-section">
        <div className="pricing-inner">
          <header className="pricing-head">
            <h2>Plans &amp; pricing</h2>
            <p className="muted">
              Pay monthly, cancel anytime. Every workspace ships with encrypted credential storage,
              a replay queue, and audit logging — plans scale seats, datasets, and support.
            </p>
          </header>

          {planState.status === "loading" && <p className="muted">Loading plans…</p>}
          {planState.status === "error" && (
            <div className="alert alert-error">{planState.message}</div>
          )}
          {planState.status === "ready" && (
            <>
              <div className="plan-grid">
                {planState.plans.map((plan) => (
                  <article
                    key={plan.id}
                    className={`plan-card${plan.recommended ? " plan-card--recommended" : ""}`}
                  >
                    {plan.recommended && (
                      <span className="plan-card__ribbon">Recommended</span>
                    )}
                    <h3 className="plan-card__name">{plan.name}</h3>
                    {plan.description && (
                      <p className="plan-card__desc">{plan.description}</p>
                    )}
                    <div className="plan-card__price">
                      <span className="plan-card__price-amount">{formatPrice(plan)}</span>
                      {effectivePrice(plan) > 0 && (
                        <span className="plan-card__price-note">USD, billed monthly</span>
                      )}
                      {plan.waiveFees && plan.priceMonthlyUsd > 0 && (
                        <span className="plan-card__price-note">
                          <span className="plan-card__waived-badge">Fees waived</span>
                          {" "}<s>${plan.priceMonthlyUsd.toLocaleString()}/mo</s>
                        </span>
                      )}
                    </div>
                    <ul className="plan-card__features">
                      {plan.features.map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      className={`btn ${plan.recommended ? "btn-primary" : ""} plan-card__cta`}
                      onClick={() => onSelectPlan(plan.id)}
                    >
                      {effectivePrice(plan) === 0 ? "Start free" : `Choose ${plan.name}`}
                    </button>
                  </article>
                ))}
              </div>

              <details className="plan-compare">
                <summary>Compare all capabilities and limits</summary>
                <div className="plan-compare__scroll">
                  <table className="plan-compare__table">
                    <thead>
                      <tr>
                        <th>Limit / capability</th>
                        {planState.plans.map((plan) => (
                          <th key={plan.id}>{plan.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="plan-compare__group">
                        <td colSpan={planState.plans.length + 1}>Limits</td>
                      </tr>
                      {LIMIT_ROWS.map((row) => (
                        <tr key={row.label}>
                          <td>{row.label}</td>
                          {planState.plans.map((plan) => (
                            <td key={plan.id}>{row.value(plan)}</td>
                          ))}
                        </tr>
                      ))}
                      <tr className="plan-compare__group">
                        <td colSpan={planState.plans.length + 1}>Capabilities</td>
                      </tr>
                      {CAPABILITY_ROWS.map((row) => (
                        <tr key={row.label}>
                          <td>{row.label}</td>
                          {planState.plans.map((plan) => (
                            <td key={plan.id} className={plan.capabilities[row.key] ? "plan-compare__yes" : "plan-compare__no"}>
                              {plan.capabilities[row.key] ? "✓" : "—"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </>
          )}

          <div className="pricing-foot">
            <Link to="/signup" className="btn btn-primary btn-lg">Create your workspace</Link>
            <span className="muted" style={{ fontSize: 13 }}>No credit card required for the free tier.</span>
          </div>
        </div>
      </section>

      <footer className="landing-foot">
        <span>© {new Date().getFullYear()} TallyStream · VitalPlatforms</span>
        <span>
          <a href="https://tallystream.vitalplatforms.com">tallystream.vitalplatforms.com</a>
        </span>
      </footer>
    </div>
  );
}
